import type {ReactElement} from 'react';
import React from 'react';
import {BaseTintOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-tint-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';
import type {RGBA} from '@Utils/color.util';

interface SlideshowItemTintOptionProps {
  onClickColorFill(): void;
}

export function SlideshowItemTintOption({...props}: SlideshowItemTintOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const tint = useGetKeyFromEffectsOfActiveSlideItem('tint');
  const tintColor = useGetKeyFromEffectsOfActiveSlideItem('tintColor');

  if (!activeItem || !activeSlideUID) {
    throw new Error('Active item or active slide is not defined in slideshow item effects options');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, got ${activeItem.gitype} instead`);
  }

  if (tint === undefined || !tintColor) {
    throw new Error('tint properties are undefined for active slide item');
  }

  const onClickHandler = (): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      effects: {
        tint: !tint,
      },
    });
  };

  const updateOpacity = (tintOpacity: number, undoable = true): void => {
    const activeSlideTintColor = tintColor.slice() as RGBA;
    activeSlideTintColor[3] = tintOpacity;
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          tintColor: activeSlideTintColor,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseTintOption
      label={activeItem.isImage() && activeItem.isDrawing() ? window.i18next.t('pmwjs_color') : window.i18next.t('pmwjs_tint')}
      tintOpacity={tintColor[3]}
      tint={tint}
      tintColor={tintColor}
      onToggleTint={onClickHandler}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
      updateOpacity={updateOpacity}
    />
  );
}
